import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import { X, Clock } from 'lucide-react';
import { format, addDays, addWeeks, addMonths } from 'date-fns';

export default function ActionItemsDialog({ open, onClose, officers, actionItemsByOfficer, onDismiss, onSnooze }) {
  const [snoozeItem, setSnoozeItem] = useState(null);
  const [customDate, setCustomDate] = useState(null);
  const sortedOfficers = [...actionItemsByOfficer].sort((a, b) => b.count - a.count);

  const handleSnooze = (email, duration) => {
    let snoozeUntil;
    const now = new Date();
    
    if (duration === 'tomorrow') {
      snoozeUntil = addDays(now, 1);
    } else if (duration === 'next_week') {
      snoozeUntil = addWeeks(now, 1);
    } else if (duration === 'next_month') {
      snoozeUntil = addMonths(now, 1);
    } else if (duration === 'custom' && customDate) {
      snoozeUntil = customDate;
    }
    
    if (snoozeUntil) {
      onSnooze(email, snoozeUntil);
      setSnoozeItem(null);
      setCustomDate(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="dash-actionitemsdialog-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-slate-100">Action Items by Officer</DialogTitle>
        </DialogHeader>
        
        <Table>
          <TableHeader>
            <TableRow className="border-slate-800">
              <TableHead className="text-slate-400">Officer</TableHead>
              <TableHead className="text-slate-400">Callsign</TableHead>
              <TableHead className="text-slate-400 text-right">Action Items</TableHead>
              <TableHead className="text-slate-400 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedOfficers.map((item) => {
              const officer = officers.find(o => o.user_email === item.email);
              return (
                <TableRow key={item.email} className="border-slate-800">
                  <TableCell className="text-slate-200">{officer?.user_email || item.email}</TableCell>
                  <TableCell className="text-slate-300">{officer?.callsign || 'N/A'}</TableCell>
                  <TableCell className="text-right">
                    <Badge className="bg-rose-500/20 text-rose-400 border-none">
                      {item.count}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onDismiss(item.email)}
                        className="h-7 text-xs text-slate-400 hover:text-red-400"
                      >
                        <X className="w-3 h-3 mr-1" />
                        Dismiss
                      </Button>
                      <Popover open={snoozeItem === item.email} onOpenChange={(open) => !open && setSnoozeItem(null)}>
                        <PopoverTrigger asChild>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSnoozeItem(item.email)}
                            className="h-7 text-xs text-slate-400 hover:text-amber-400"
                          >
                            <Clock className="w-3 h-3 mr-1" />
                            Snooze
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="bg-slate-800 border-slate-700 w-64">
                          <div className="space-y-3">
                            <Label className="text-slate-200 text-sm font-semibold">Snooze until:</Label>
                            <div className="space-y-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="w-full justify-start border-slate-700 text-slate-200 hover:bg-slate-700"
                                onClick={() => handleSnooze(item.email, 'tomorrow')}
                              >
                                Tomorrow
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="w-full justify-start border-slate-700 text-slate-200 hover:bg-slate-700"
                                onClick={() => handleSnooze(item.email, 'next_week')}
                              >
                                Next Week
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="w-full justify-start border-slate-700 text-slate-200 hover:bg-slate-700"
                                onClick={() => handleSnooze(item.email, 'next_month')}
                              >
                                Next Month
                              </Button>
                              <div className="pt-2 border-t border-slate-700">
                                <Label className="text-slate-400 text-xs mb-2 block">Select Custom Date</Label>
                                <Calendar
                                  mode="single"
                                  selected={customDate}
                                  onSelect={setCustomDate}
                                  className="rounded-md border border-slate-700 bg-slate-900"
                                  disabled={(date) => date < new Date()}
                                />
                                {customDate && (
                                  <Button
                                    size="sm"
                                    className="w-full mt-2 bg-amber-600 hover:bg-amber-700"
                                    onClick={() => handleSnooze(item.email, 'custom')}
                                  >
                                    Snooze until {format(customDate, 'MMM d, yyyy')}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  );
}